// OTTERGA-2666 — TEMPORARY TEST INSTRUMENTATION.
// Fire-and-forget shipper that POSTs structured events to the same-origin
// `/logging` path, which CloudFront routes (OAC-signed) to the client-logging
// Lambda Function URL -> CloudWatch. The environment/stream is derived at the
// edge from the `x-otter-source` header, never from this payload.
//
// This is a throwaway probe to confirm the ingestion path works end-to-end, not
// the permanent client log sender. Logging must never throw or affect the app.

import { platformTitle } from './helpers'

const LOGGING_PATH = '/logging'

// Hex-encoded SHA-256 of the request body. OAC-protected Lambda function URLs
// reject unsigned payloads, so for POST the viewer must send this in the
// `x-amz-content-sha256` header; CloudFront then signs the origin request with
// it. Same-origin, so the custom header triggers no CORS preflight. See:
// https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-lambda.html
async function bodyHash(body) {
  const bytes = new TextEncoder().encode(body)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function send(events) {
  if (!events.length) return
  const body = JSON.stringify({ events })
  // Fire-and-forget; a logging error (incl. missing crypto.subtle) must never
  // surface to the user.
  bodyHash(body)
    .then((hash) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', LOGGING_PATH)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.setRequestHeader('x-amz-content-sha256', hash)
      xhr.onerror = () => {}
      xhr.send(body)
    })
    .catch(() => {
      // empty block
    })
}

function defaultContext() {
  const context = {
    platform: platformTitle(),
    host: window.location.host,
    route: window.location.pathname,
  }
  if (BUILD_NUMBER) {
    context.build = BUILD_NUMBER
  }
  return context
}

// level: 'error' | 'warn' | 'info' | 'debug'
export function logClientEvent(level, message, context = {}) {
  send([
    {
      level,
      message,
      ts: Date.now(),
      context: { ...defaultContext(), ...context },
    },
  ])
}
